/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import { useHookstate, type State } from '@hookstate/core'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Collapse,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import React, { useEffect } from 'react'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import FilterAutocompleteStations from '~/components/FilterAutocompleteStations'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import FilterAutocompleteDevice from '~/components/FilterAutocompleteDevice'
import { useLang } from '~/hooks/useLang'

interface AuditUserLoginLogoutFiltersProps {
  readonly hidden?: boolean
  readonly labelButton?: State<string>
  readonly loadingButton: State<boolean>
  readonly selectLoginLogout?: State<string | null>
  readonly resetValues?: State<boolean>
  readonly focusChange?: State<boolean>
  readonly userId?: State<number | null>
  readonly stationId?: State<number | null>
  readonly initialPeriod: State<string>
  readonly onPeriodChange: (value: string) => void
  readonly onStartDateChange: (value: string | null) => void
  readonly onFinishDateChange: (value: string | null) => void
  readonly clearFilters: () => void
  readonly searchButton?: () => void
  readonly originType: State<string | null>
  readonly selectedDevice: State<number | null>
  readonly selectedStations: State<number | null>
}

export default function AuditUserLoginLogoutFilters({
  hidden,
  labelButton,
  loadingButton,
  selectLoginLogout,
  userId,
  resetValues,
  focusChange,
  initialPeriod,
  onStartDateChange,
  onFinishDateChange,
  onPeriodChange,
  clearFilters,
  searchButton,
  originType,
  selectedDevice,
  selectedStations,
}: AuditUserLoginLogoutFiltersProps) {
  const { translate } = useLang()
  const selectTypeSearch = useHookstate<string | null>('1')
  const selectTypeSearchValue = selectTypeSearch?.get()
  const selectLoginLogoutValue = selectLoginLogout?.get()

  useEffect(() => {
    switch (String(selectTypeSearchValue)) {
      case '1':
        selectLoginLogout?.set(null)
        break
      case '2':
        selectLoginLogout?.set('creation')
        break
      case '3':
        selectLoginLogout?.set('view')
        break
      case '4':
        selectLoginLogout?.set('forced_expiration')
        break
      case '5':
        selectLoginLogout?.set('validation')
        break
      default:
        selectLoginLogout?.set(null)
    }
  }, [
    selectTypeSearchValue,
    selectTypeSearch,
    selectLoginLogout,
  ])

  return (
    <Collapse in={hidden}>
      <Grid
        container
        xs={12}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Grid container direction={'column'} display={'flex'} gap={1}>
          <Grid container direction={'row'} flexWrap={'wrap'}>
            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel
                  id="select-origin-type-label"
                  shrink={originType.get() !== null}
                >
                  {translate('Origem')}
                </InputLabel>
                <Select
                  labelId="select-origin-type-label"
                  id="select-origin-type"
                  value={originType.get() ?? ''}
                  label={translate('Origem')}
                  displayEmpty
                  notched={originType.get() !== null}
                  onChange={(event) => {
                    const value = event.target.value

                    originType.set(value as string)

                    selectedDevice.set(null)
                    selectedStations.set(null)
                  }}
                  endAdornment={
                    originType.get() !== null ? (
                      <IconButton
                        size="small"
                        sx={{ marginRight: 2 }}
                        onClick={() => {
                          originType.set(null)
                          selectedDevice.set(null)
                          selectedStations.set(null)
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  <MenuItem value="station">{translate('Estação')}</MenuItem>
                  <MenuItem value="device">{translate('Dispositivo')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {originType.get() === 'device' ? (
              <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                <FilterAutocompleteDevice
                  onDeviceChange={(item) => selectedDevice.set(item)}
                  deviceId={selectedDevice.get()}
                />
              </Grid>
            ) : originType.get() === 'station' ? (
              <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                <FilterAutocompleteStations
                  onStationsChange={(value) =>
                    selectedStations?.set(value as number | null)
                  }
                  stationsId={
                    selectedStations?.get()
                      ? selectedStations.get()
                      : null
                  }
                  disabled={false}
                  single
                />
              </Grid>
            ) : undefined}
            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FilterAutocompleteFetch
                label={translate('User')}
                urlFetch={'/user/get'}
                dataRequest={'user'}
                size={'small'}
                disabled={false}
                onFetchChange={(user: number | null) => userId?.set(user)}
                resetValues={resetValues?.get()}
                focusChange={focusChange}
                valueId={userId?.get() ? Number(userId?.get()) : undefined}
              />
            </Grid>

            <FilterSelectPeriod
              onStartDateChange={(value) =>
                onStartDateChange(value?.toJSON() ?? null)
              }
              onFinishDateChange={(value) =>
                onFinishDateChange(value?.toJSON() ?? null)
              }
              onPeriodChange={onPeriodChange}
              period={initialPeriod.get()}
              xsDateBetween={2}
              xsPeriod={2}
              hidePeriod={false}
              marginDateBetween={0}
              disableFuture={false}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction={'row'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          pt: 4,
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            clearFilters()
            selectTypeSearch?.set('1')
            originType.set(null)
            selectedDevice.set(null)
            selectedStations.set(null)
          }}
        >
          {translate('Clear Filters')}
        </Button>
        <LoadingButton
          loading={loadingButton.get()}
          variant="contained"
          onClick={searchButton}
        >
          {labelButton?.get()}
        </LoadingButton>
      </Grid>
    </Collapse>
  )
}
